import React from "react";
import { motion } from "framer-motion";
import { Globe2, Landmark } from "lucide-react";
import { SUSI } from "../lib/siteData";

const reveal = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Susi() {
  return (
    <section
      id="susi"
      data-testid="susi-section"
      className="py-20 sm:py-28 bg-ivory relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(217,119,6,0.10), transparent 65%)" }}
      />
      <div className="container-prose relative">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="max-w-3xl"
        >
          <p className="overline text-saffron flex items-center gap-2">
            <Globe2 size={14} /> 08 — Global Fellowship
          </p>
          <h2 className="h2-display mt-4 text-4xl sm:text-5xl text-ink">
            A Fulbright-style summer in <span className="italic text-saffron">Montana</span>.
          </h2>
          <p className="mt-6 body-prose text-ink/75 text-base sm:text-lg">{SUSI.intro}</p>
        </motion.div>

        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mt-10 border-l-2 border-saffron pl-5 max-w-2xl"
        >
          <p className="text-ink text-lg" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
            {SUSI.program}
          </p>
          <p className="mt-2 text-sm text-ink/70 flex items-start gap-2">
            <Landmark size={15} className="mt-0.5 text-forest shrink-0" />
            <span>
              {SUSI.host}
              <br />
              <span className="text-[11px] uppercase tracking-[0.22em] text-ink/50">
                {SUSI.sponsor}
              </span>
            </span>
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5 space-y-8">
            {SUSI.highlights.map((h, i) => (
              <motion.div
                key={h.title}
                data-testid={`susi-highlight-${i}`}
                custom={i}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                className="border-t border-ink/12 pt-5"
              >
                <p className="text-[11px] uppercase tracking-[0.28em] text-saffron">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3
                  className="mt-2 text-2xl text-ink"
                  style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 500 }}
                >
                  {h.title}
                </h3>
                <p className="mt-2 body-prose text-ink/70 text-base">{h.body}</p>
              </motion.div>
            ))}
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 gap-3 sm:gap-4">
            {SUSI.photos.map((p, i) => (
              <motion.figure
                key={p.url}
                data-testid={`susi-photo-${i}`}
                custom={i}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                className={`group relative overflow-hidden bg-ink/5 ${
                  i === 0 ? "aspect-[4/5] col-span-2 sm:col-span-1" : "aspect-[4/5]"
                } ${i === 3 ? "col-span-2 sm:col-span-1" : ""}`}
              >
                <img
                  src={p.url}
                  alt={p.caption}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                />
                <figcaption className="absolute bottom-0 inset-x-0 px-4 py-3 bg-gradient-to-t from-ink/85 to-transparent text-ivory text-[12px] tracking-wide">
                  {p.caption}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
